// src/pages/SetPassword.js — public route that invite and reset emails land on
// (redirectTo = FRONTEND_URL/set-password). The Supabase client turns the
// link's token into a session (detectSessionInUrl), then the user chooses a
// password, which posts to /api/auth/set-password. ProtectedRoute also sends
// any signed-in user whose users.password_set_at is null here, so new users
// can't skip it.
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import AuthShell from '../components/AuthShell'
import PasswordForm from '../components/PasswordForm'

// Read once at load, before the Supabase client clears the hash. An expired or
// already-used link arrives as #error=...&error_description=...
function linkErrorFromUrl() {
  const params = new URLSearchParams(window.location.hash.replace(/^#/, '') || window.location.search)
  const desc = params.get('error_description')
  return desc ? desc.replace(/\+/g, ' ') : null
}

export default function SetPassword() {
  const { user, profile, loading, signOut } = useAuth()
  const [linkError] = useState(linkErrorFromUrl)

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner spinner-lg" />
      </div>
    )
  }

  if (!user) {
    return (
      <AuthShell title="This link has expired" subtitle={linkError || 'The link is invalid, expired or has already been used.'}>
        <Link to="/forgot-password" className="btn btn-primary" style={{ width: '100%', padding: '10px', textAlign: 'center' }}>
          Send a new link
        </Link>
        <Link to="/login" style={{ fontSize: 12, color: 'var(--accent)' }}>Back to sign in</Link>
      </AuthShell>
    )
  }

  const isNew = profile && !profile.password_set_at
  return (
    <AuthShell
      centerHeader
      title={isNew ? 'Set your password' : 'Set a new password'}
      subtitle={isNew
        ? `Welcome${profile?.name ? `, ${profile.name.split(' ')[0]}` : ''}. Choose a password to finish setting up your account (${user.email}).`
        : `Choose a new password for ${user.email}.`}
    >
      <PasswordForm
        centerSubmit
        submitLabel={isNew ? 'Set password and continue' : 'Save new password'}
        // Hard navigation: a fresh boot reloads the profile (now with
        // password_set_at) so ProtectedRoute lets them through by role.
        onSuccess={() => window.location.assign('/')}
      />
      <button type="button" onClick={() => signOut().then(() => window.location.assign('/login'))}
        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 12, color: 'var(--text-muted)', alignSelf: 'center' }}>
        Not you? Sign out
      </button>
    </AuthShell>
  )
}
